import { useMemo } from 'react';
import { useLocation } from 'react-router';

import { type WhatsAppContactId } from '@/config/whatsappContacts';
import { usePageWhatsAppContactOverride } from '@/stores/pageContextStore';
import { type ResolvedWhatsAppContact, resolveWhatsAppContact } from '@/utils/whatsappContact';

/**
 * The seat answering for the current page, or null when WhatsApp is switched
 * off site-wide.
 *
 * The twin of `usePageWhatsAppMessage`, and resolved the same way on purpose:
 * that hook decides what a WhatsApp link says, this one decides who it reaches,
 * and both read the route rather than being passed down. Which is why adding
 * per-page numbers needed no new props anywhere — `WhatsAppButton` calls this
 * itself, so all 25 call sites were already routing by the time the map existed.
 *
 * `contactId` is the escape hatch for a control that is not about the page it
 * sits on. Nothing passes it yet (page-level routing only, decided
 * 2026-10-17); it is the seam that keeps a parts card on a sales page from
 * needing one.
 *
 * Memoised because it returns an object: a resolved seat is a new object every
 * render otherwise, which would defeat any memo downstream that depends on one.
 */
export function useWhatsAppContact(
  contactId?: WhatsAppContactId | null,
): ResolvedWhatsAppContact | null {
  const { pathname } = useLocation();
  const pageContactId = usePageWhatsAppContactOverride();

  return useMemo(
    () => resolveWhatsAppContact({ contactId, pageContactId, pathname }),
    [contactId, pageContactId, pathname],
  );
}
